<template>
    <div class="w-full">
        <div class="w-full border border-gray-200">
            <div class="p-6">
                <div class="w-full mb-4">
                    <div class="flex flex-wrap items-center mt-4 bg-tan-200 border border-b-0">
                        <div class="inline-block px-4 py-2 font-bold text-base"><i class="far fa-clock mr-2"></i>Last Update: {{ formattedLastUpdated }}</div>
                    </div>
                    <table class="table table-hover border border-t-0 admin-table">
                        <thead>
                            <tr class="bg-gray-super-100">
                                <th class="text-center w-12">Sort</th>
                                <th class="text-center w-12">Status</th>
                                <th class="text-center w-[10%]">Symbol</th>
                                <th class="w-[20%]">Commodity Name</th>
                                <th class="text-center w-[12%]">Month/Year</th>
                                <th class="text-center">Notes</th>
                                <th width="50"></th>
                            </tr>
                        </thead>
                        <tbody v-if="!form.positions.length">
                            <tr>
                                <td colspan="7" class="text-center py-4">
                                    No positions added yet.
                                </td>
                            </tr>
                        </tbody>
                        <draggable v-model="form.positions" tag="tbody" handle=".canDrag" item-key="_key">
                            <template #item="{ element: position, index }">
                                <tr>
                                    <td class="text-center cursor-move canDrag">
                                        <i class="fas fa-arrows-alt text-gray-500"></i>
                                    </td>
                                    <td class="text-center">
                                        <toggle-switch class="ml-2 tgl-dark" v-model="position.active" :value="position.active" :clickable="true" />
                                    </td>
                                    <td>
                                        <input v-model.trim="position.symbol" @input="position.symbol = position.symbol.toUpperCase()" type="text" class="form-input form-input-sm" maxlength="50" :class="{ 'is-invalid': !position.symbol?.trim() }" />
                                    </td>
                                    <td>
                                        <input v-model.trim="position.symbol_name" type="text" maxlength="255" class="form-input form-input-sm" />
                                    </td>
                                    <td>
                                        <input v-model.trim="position.month_year" type="text" class="form-input form-input-sm" />
                                    </td>
                                    <td>
                                        <input v-model="position.notes" type="text" class="form-input form-input-sm" />
                                    </td>
                                    <td class="text-center">
                                        <a class="btn btn-default btn-shadow-none btn-red-ghost btn-xs ml-1 mb-1 md:mb-0" title="Delete Item" @click="removePosition(index)"><i class="fas fa-times"></i></a>
                                    </td>
                                </tr>
                            </template>
                        </draggable>
                    </table>
                    <div class="mt-4">
                        <button type="button" class="btn btn-default" @click="addPosition">Add Symbol</button>
                    </div>
                </div>

                <div class="alert alert-warning mt-12 mb-4 text-sm">
                    <h3>Summary</h3>
                    <p>&bull; To enter a link for a chart type #.</p>
                    <p>&bull; To enter a link to text select the text you want to add the link to.</p>
                    <p>&bull; Drag and drop images where you would like them placed.</p>
                </div>
                <div class="w-full mb-4">
                    <w-editor class="pt-2 border-t" :content="form.summary" v-model="form.summary"></w-editor>
                </div>

                <div class="w-full mt-8">
                    <button type="button" @click="handleSave" class="btn btn-yellow" :disabled="saving || hasInvalidRows">{{ saving ? 'Saving...' : 'Save' }}</button>
                    <a href="/admin/products/positions" class="text-black ml-2 btn btn-default">Cancel</a>
                </div>
            </div>
        </div>

        <flash-message ref="flash"></flash-message>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Draggable from 'vuedraggable';
import ToggleSwitch from '../Elements/ToggleSwitch';

const props = defineProps({
    initialValues: {
        type: [Object, Array],
        default: () => ({})
    }
});

const flash = ref(null);
const saving = ref(false);
const lastUpdated = ref(props.initialValues.last_updated || '');
const form = ref({
    positions: (props.initialValues.positions || []).map(
        item => ({
            id: item.id,
            _key: crypto.randomUUID(),
            active: !!item.active,
            symbol: item.symbol ?? '',
            symbol_name: item.symbol_name ?? '',
            month_year: item.month_year ?? '',
            notes: item.notes ?? '',
        })
    ),
    summary: props.initialValues.summary ?? '',
});

const formattedLastUpdated = computed(() => {
    if (!lastUpdated.value) {
        return '-';
    }

    return new Date(lastUpdated.value)
        .toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
});

const hasInvalidRows = computed(() => {
    return form.value.positions.some(position => {
        return (
            !position.symbol?.trim()
            || !position.symbol_name?.trim()
        );
    });
});

function addPosition()
{
    form.value.positions.push({
        id: null,
        _key: crypto.randomUUID(),
        active: true,
        symbol: '',
        symbol_name: '',
        month_year: '',
        notes: '',
    });
}

function removePosition(index)
{
    form.value.positions.splice(index, 1);
}

async function handleSave()
{
    if (saving.value) {
        return;
    }

    saving.value = true;

    try {
        const route = window.route('admin.positions.update');

        const payload = {
            summary: form.value.summary,
            positions: form.value.positions.map(
                ({ _key, ...position }) => ({
                    ...position,
                    symbol: position.symbol?.trim()?.toUpperCase() || '',
                    symbol_name: position.symbol_name?.trim() || '',
                    month_year: position.month_year?.trim() || '',
                    notes: position.notes?.trim() || '',
                })
            )
        };

        const response = await axios.post(
            route,
            payload
        );

        flash.value.display(
            'Successfully saved CommodityTrends Trading Plan!'
        );

        lastUpdated.value = response.data.last_updated;
        form.value.positions = response.data.positions.map(
            item => ({
                id: item.id,
                _key: crypto.randomUUID(),
                active: !!item.active,
                symbol: item.symbol ?? '',
                symbol_name: item.symbol_name ?? '',
                month_year: item.month_year ?? '',
                notes: item.notes ?? '',
            })
        );
    } catch (error) {
        flash.value.error('Error updating Positions!');

    } finally {
        saving.value = false;
    }
}
</script>
